import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import { withDefaultDates } from "@/utils";
import { useInsightView } from "@/hooks/useInsightView";
import { useSearchSectionRoutes } from "@/hooks/useSearchSectionRoutes";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import { omitPageParams, usePageParam } from "@/hooks/usePageParam";
import { useI18n } from "@/contexts/i18nContext";
import { differenceInDays, isSameHour } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { getSearchMentionsV4 } from "@/api/endpoints/searches";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@components/ui/sheet";
import {
  formatDateFromMCPMonitorTimeSeriesGranularity,
  getMCPMonitorTimeSeriesGranularityFromDayDiff,
} from "@components/widgets/utils";
import { plural } from "@lingui/core/macro";
import { useEffect, useState } from "react";
import MentionCards from "@components/MentionCards";

export function MentionsSheetView() {
  const { formatDateRange, formatDate } = useI18n();

  // URL state
  const insight = useInsightView();
  const section = useSearchSectionRoutes();

  const {
    uuid: searchUuid,
    orgUuid,
    startDate,
    endDate,
  } = useParams<{
    startDate: string;
    endDate: string;
    orgUuid: Uuid;
    uuid: Uuid;
  }>();

  const [_filters] = useFilterParamsV4();
  const [page, setPage] = usePageParam("sheet_page");
  const history = useHistory();
  const location = useLocation();

  const filters = withDefaultDates(_filters);

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Server state
  const {
    data: mentionCardsData,
    error: mentionCardsError,
    isLoading: isMentionCardsLoading,
  } = useQuery({
    queryKey: [searchUuid, "mentions", startDate, endDate, filters, page],
    queryFn: () =>
      getSearchMentionsV4(searchUuid, {
        ...filters,
        start_date: startDate,
        end_date: endDate,
        full_count: true,
        page,
      }),
  });

  // Helpers
  const count = mentionCardsData?.ok ? mentionCardsData.data.count : 0;

  const diffInDays = differenceInDays(filters.end_date, filters.start_date);
  const granularity = getMCPMonitorTimeSeriesGranularityFromDayDiff(diffInDays);

  const lede = formatDateRange(new Date(startDate), new Date(endDate), "PPP", {
    allowSameDay: isSameHour(startDate, endDate),
  });

  const closeTo = {
    overview: section.detail,
    reach: section.insightsReach,
    sentiment: section.insightsSentiment,
    results: section.insightsResults,
  }[insight];

  // Handlers
  function handleOpenChangeComplete(newIsOpen: boolean) {
    if (newIsOpen) {
      return;
    }

    history.replace({
      pathname: generatePath(closeTo, {
        uuid: searchUuid,
        orgUuid,
      }),
      search: omitPageParams(location.search, ["sheet_page"]),
    });
  }

  // Side-effects
  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsOpen(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={setIsOpen}
      onOpenChangeComplete={handleOpenChangeComplete}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {formatDateFromMCPMonitorTimeSeriesGranularity(
              startDate,
              granularity,
              formatDate
            )}
          </SheetTitle>
          <SheetDescription>
            {count} {plural(count, { one: "inlägg", other: "inlägg" })}
            {lede ? ` · ${lede}` : null}
          </SheetDescription>
        </SheetHeader>

        <div className="overflow-y-auto px-6 pb-6 flex-1 pt-px">
          <MentionCards
            isMentionCardsLoading={isMentionCardsLoading}
            mentionCardsError={mentionCardsError}
            mentionCardsData={mentionCardsData}
            onChangePage={setPage}
            orgUuid={orgUuid}
            page={page}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
